import {BrowserModule} from '@angular/platform-browser';
import {NgModule} from '@angular/core';
import {AppRoutingModule} from './app-routing.module';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {CommonModule} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';

// i18n
import {TranslateModule} from '@ngx-translate/core';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {AppComponent} from './components/app.component';
// Page components
import {LoginComponent} from './components/pages/login/login.component';
import {HomeComponent} from './components/pages/home/home.component';
import {AlertsViewComponent} from './components/pages/alerts-view/alerts-view.component';
import {MissionSelectionComponent} from './components/pages/mission-selection/mission-selection.component';
import {MissionConfigComponent} from './components/pages/mission-config/mission-config.component';
import {MissionConfigMenuComponent} from './components/pages/mission-config-menu/mission-config-menu.component';
import {MaterialConfigComponent} from './components/pages/material-config/material-config.component';
import {ProductConfigComponent} from './components/pages/product-config/product-config.component';
import {TeamConfigComponent} from './components/pages/team-config/team-config.component';
import {SupportConfigComponent} from './components/pages/support-config/support-config.component';
import {ContainersComponent} from './components/pages/containers/containers.component';
import {TeamsComponent} from './components/pages/teams/teams.component';
import {MissionInfoComponent} from './components/pages/mission-info/mission-info.component';
// Common components
import {MapComponent} from './components/common/map/map.component';
import {AlertsComponent} from './components/common/alerts/alerts.component';
import {AlertComponent} from './components/common/alert/alert.component';
import {LoaderComponent} from './components/common/loader/loader.component';
import {MenuComponent} from './components/common/menu/menu.component';
import {AddContainerComponent} from './components/common/add-container/add-container.component';
import {AddSkidComponent} from './components/common/add-skid/add-skid.component';
import {AddAnalyzerComponent} from './components/common/add-analyzer/add-analyzer.component';
import {AddTeamComponent} from './components/common/add-team/add-team.component';
import {ConfirmationModalComponent} from './components/common/confirmation-modal/confirmation-modal.component';
import {AddGatewayComponent} from './components/common/add-gateway/add-gateway.component';
import {AddPumpComponent} from './components/common/add-pump/add-pump.component';
import {AddFlowmeterComponent} from './components/common/add-flowmeter/add-flowmeter.component';
import {MaterialValidationComponent} from './components/common/material-validation/material-validation.component';
import {CreateContainerComponent} from './components/common/create-container/create-container.component';
import {CreateTeamComponent} from './components/common/create-team/create-team.component';
import {UpdateTeamComponent} from './components/common/update-team/update-team.component';
import {AddSitewellawareidComponent} from './components/common/add-sitewellawareid/add-sitewellawareid.component';
import {SelectMarketComponent} from './components/common/select-market/select-market.component';
import {
  UpdateMissionProductComponent
} from './components/common/update-mission-product/update-mission-product.component';
// Services
import {AuthService} from './providers/services/auth.service';
import {MissionsService} from './providers/services/missions.service';
import {AlertsService} from './providers/services/alerts.service';
import {LanguageService} from './providers/services/language.service';
import {LoaderService} from './providers/services/loader.service';
import {ContainersService} from './providers/services/containers.service';
import {LoadingStationsService} from './providers/services/loading-stations.service';
import {ProductsService} from './providers/services/products.service';
import {SkidsService} from './providers/services/skids.service';
import {AnalyzersService} from './providers/services/analyzers.service';
import {TeamsService} from './providers/services/teams.service';
import {MaterialsService} from './providers/services/materials.service';
import {ConfirmationModalService} from './providers/services/confirmation-modal.service';
import {GatewaysService} from './providers/services/gateways.service';
import {PumpsService} from './providers/services/pumps.service';
import {FlowmetersService} from './providers/services/flowmeters.service';
import {ApiService} from './providers/services/api.service';
import {MarketService} from './providers/services/market.service';
import {StorageService} from './providers/services/storage.service';
import {ReactorsService} from './providers/services/reactors.service';
// Interceptors
import {TokenInterceptor} from './providers/interceptors/token.interceptor';
// Pipes
import {StatusLabelPipe} from './providers/pipes/status-label.pipe';
import {QuestionnaireTimestampToDatePipe, TimestampToDatePipe} from './providers/pipes/timestamp-to-date.pipe';
import {ContainerWarningPipe} from './providers/pipes/container-warning.pipe';
import {SanitizeHtmlPipe} from './providers/pipes/sanitizer-html.pipe';
import {MissionCreateComponent} from './components/pages/mission-create/mission-create.component';
import {AppMaterialModule} from "./app-material.module";
import {HighlightPipe} from "./providers/pipes/highlight.pipe";
import {
  ArkemaInputWithUnitComponent
} from "./components/common/arkema-input-with-unit/arkema-input-with-unit.component";
import {ChipStatusComponent} from "./components/common/chip-status/chip-status.component";
import {MissionComponent} from "./components/pages/mission/mission.component";
import { ParticipantsConfigComponent } from './components/pages/participants-config/participants-config.component';
import { CreateParticipantComponent } from './components/common/create-participant/create-participant.component';

@NgModule({ declarations: [
        AppComponent,
        MapComponent,
        LoginComponent,
        AlertsComponent,
        AlertsViewComponent,
        AlertComponent,
        HomeComponent,
        MissionConfigComponent,
        MaterialConfigComponent,
        ProductConfigComponent,
        LoaderComponent,
        MissionSelectionComponent,
        MenuComponent,
        AddContainerComponent,
        MissionConfigMenuComponent,
        StatusLabelPipe,
        TimestampToDatePipe,
        ContainerWarningPipe,
        SanitizeHtmlPipe,
        AddSkidComponent,
        AddAnalyzerComponent,
        TeamConfigComponent,
        AddTeamComponent,
        ConfirmationModalComponent,
        AddGatewayComponent,
        AddPumpComponent,
        AddFlowmeterComponent,
        MaterialValidationComponent,
        SupportConfigComponent,
        ContainersComponent,
        CreateContainerComponent,
        TeamsComponent,
        CreateTeamComponent,
        UpdateTeamComponent,
        AddSitewellawareidComponent,
        SelectMarketComponent,
        MissionInfoComponent,
        UpdateMissionProductComponent,
        MissionCreateComponent,
        HighlightPipe,
        QuestionnaireTimestampToDatePipe,
        ArkemaInputWithUnitComponent,
        ChipStatusComponent,
        MissionComponent,
        ParticipantsConfigComponent,
        CreateParticipantComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        TranslateModule.forRoot(),
        BrowserAnimationsModule,
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        AppMaterialModule], providers: [
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        AuthService,
        MissionsService,
        AlertsService,
        LanguageService,
        LoaderService,
        ContainersService,
        LoadingStationsService,
        ProductsService,
        SkidsService,
        AnalyzersService,
        TeamsService,
        MaterialsService,
        ConfirmationModalService,
        GatewaysService,
        PumpsService,
        FlowmetersService,
        ApiService,
        MarketService,
        StorageService,
        ReactorsService,
        ...provideTranslateHttpLoader({
            prefix: './assets/i18n/',
            suffix: '.json'
        }),
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
    ] })

export class AppModule { }
